import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import apps from "../data/apps.js";

const scenes = [...apps, { cta: true }];

const stores = {
  appStore: { label: "App Store", urlField: "appStoreUrl" },
  playStore: { label: "Google Play", urlField: "playStoreUrl" },
  web: { label: "Web", urlField: "webUrl", action: "Try it on" },
  download: { label: "Desktop & Mobile", urlField: "downloadUrl", action: "Download for" },
  android: { label: "Android", urlField: "androidUrl", action: "Download for" },
};

const StoreButton = ({ label, url, action = "Get it on" }) =>
  url ? (
    <a className="store-btn" href={url} target="_blank" rel="noreferrer">
      <small>{action}</small>
      {label}
    </a>
  ) : (
    <span className="store-btn store-btn-soon">
      <small>Soon on</small>
      {label}
    </span>
  );

const AppScene = ({ app, index }) => (
  <div className="scene-card">
    <div className="scene-visual">
      <div className={`scene-device scene-device-${app.imageKind}`}>
        <img src={app.image} alt={`${app.name} artwork`} />
      </div>
      <div className="scene-ring" />
    </div>
    <div className="scene-info">
      <span className="scene-index">{String(index + 1).padStart(2, "0")}</span>
      <span className="scene-category">{app.category}</span>
      <h3 className="scene-name">{app.name}</h3>
      <p className="scene-desc">{app.description}</p>
      {app.platforms.length > 0 && (
        <div className="store-row">
          {app.platforms.map((key) => (
            <StoreButton
              key={key}
              label={stores[key].label}
              url={app[stores[key].urlField]}
              action={stores[key].action}
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

const CtaScene = () => (
  <div className="scene-card scene-cta">
    <span className="scene-index">{String(scenes.length).padStart(2, "0")}</span>
    <h3 className="scene-name">
      Your app <span className="accent">could be next.</span>
    </h3>
    <p className="scene-desc">
      Got a game or app that deserves a bigger stage? We co-publish with independent developers —
      from store launch to growth.
    </p>
    <a href="#contact" className="btn">
      Pitch us your app <span aria-hidden="true">→</span>
    </a>
  </div>
);

// Her sahne derinlikten kameraya yaklaşır, bir süre durur, sonra kameranın arkasına uçar.
const Scene = ({ progress, index, count, children }) => {
  const seg = 1 / count;
  const start = index * seg;
  const isLast = index === count - 1;
  const input = isLast
    ? [start - seg, start, 1]
    : [start - seg, start, start + seg * 0.5, start + seg];

  const z = useTransform(progress, input, isLast ? [-1600, 0, 0] : [-1600, 0, 0, 900]);
  const rotateX = useTransform(progress, input, isLast ? [18, 0, 0] : [18, 0, 0, -25]);
  // Uzaktaki sahne geç belirsin ki öndeki sahnenin metniyle üst üste binmesin.
  const fadeIn = [start - seg * 0.45, start - seg * 0.1];
  const opacity = useTransform(
    progress,
    isLast ? [...fadeIn, 1] : [...fadeIn, start + seg * 0.5, start + seg * 0.85],
    isLast ? [0, 1, 1] : [0, 1, 1, 0]
  );
  const pointerEvents = useTransform(opacity, (o) => (o > 0.6 ? "auto" : "none"));

  return (
    <motion.div className="scene" style={{ z, rotateX, opacity, pointerEvents }}>
      {children}
    </motion.div>
  );
};

const ProgressDot = ({ progress, index, count }) => {
  const seg = 1 / count;
  const scale = useTransform(
    progress,
    [index * seg - seg * 0.5, index * seg, index * seg + seg * 0.5],
    [1, 2.2, 1]
  );
  return <motion.span className="tunnel-dot" style={{ scaleY: scale }} />;
};

const AppsTunnel = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const bgShift = useTransform(scrollYProgress, [0, 1], ["0%", "-50%"]);
  const ghostX = useTransform(scrollYProgress, [0, 1], ["10%", "-60%"]);

  return (
    <section
      id="apps"
      className="tunnel"
      ref={ref}
      style={{ height: `${(scenes.length + 0.5) * 100}vh` }}
    >
      <div className="tunnel-sticky">
        <motion.div className="tunnel-floor" style={{ backgroundPositionY: bgShift }} />
        <motion.div className="tunnel-ghost" style={{ x: ghostX }} aria-hidden="true">
          OUR APPS — OUR APPS — OUR APPS
        </motion.div>
        <div className="tunnel-head">
          <span className="eyebrow">
            <span className="dot" /> Portfolio
          </span>
        </div>
        <div className="tunnel-stage">
          {scenes.map((scene, i) => (
            <Scene key={i} progress={scrollYProgress} index={i} count={scenes.length}>
              {scene.cta ? <CtaScene /> : <AppScene app={scene} index={i} />}
            </Scene>
          ))}
        </div>
        <div className="tunnel-progress">
          {scenes.map((_, i) => (
            <ProgressDot key={i} progress={scrollYProgress} index={i} count={scenes.length} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppsTunnel;
